import { useEffect } from 'react';

import { authTokenStore, TOKEN_REFRESH_SKEW_MS } from 'app/shared/auth';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';

/**
 * Wires the module-level {@link authTokenStore} to the host SDK so the API
 * layer can replace the access token, before or after it expires, without a
 * full iframe reload.
 *
 * The store lives outside React because `ApiProvider` (which reads the token)
 * sits above `MiniappSdkProvider` (which owns the SDK). This hook, mounted
 * under the SDK provider, seeds the SDK with the launch token and registers
 * `getValidToken` as the store's refresher.
 *
 * The SDK owns the host round-trip: it picks the command this host can answer
 * (`refreshToken` where the host advertises or bridges it, otherwise
 * `getJWT`), times out, merges concurrent refreshes and ignores tokens that are
 * already dead. A failure rejects with `TokenRefreshError`; the store keeps
 * the current token, and the failing request surfaces through the
 * session-expired screen. The skew is passed explicitly so the store and the
 * SDK agree on what "stale" means.
 */
export const useAuthTokenSync = (): void => {
  const sdk = useMiniappSdk();

  useEffect(() => {
    if (!sdk) return undefined;

    sdk.seedToken(authTokenStore.peekToken());
    authTokenStore.registerRefresher(({ force }) =>
      sdk.getValidToken({ forceRefresh: force, skew: TOKEN_REFRESH_SKEW_MS }));

    return () => {
      authTokenStore.invalidateRefresh();
      authTokenStore.registerRefresher(null);
    };
    // `MiniappSdkProvider` creates the client once and never replaces it.
  }, [sdk]);
};
